import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { api, ApiError } from '@/api/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/toast';
import { AdminPageHeader } from '@/components/admin/AdminPageHeader';

export const Route = createFileRoute('/admin/stripe')({
  component: AdminStripe,
});

interface StripeSettingsView {
  publishableKey: string | null;
  secretKeySet: boolean;
  secretKeyLast4: string | null;
  webhookSecretSet: boolean;
  upfrontFeeCents: number;
  deferredFeeCents: number;
  currency: string;
}

function AdminStripe() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const settings = useQuery({
    queryKey: ['admin', 'stripe-settings'],
    queryFn: () => api<StripeSettingsView>('/admin/settings/stripe'),
    retry: false,
  });

  const [publishableKey, setPublishableKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [upfront, setUpfront] = useState('79');
  const [deferred, setDeferred] = useState('99');
  const [currency, setCurrency] = useState('aud');

  // Seed the form from the server once (secrets stay blank — they're write-only).
  useEffect(() => {
    const d = settings.data;
    if (!d) return;
    setPublishableKey(d.publishableKey ?? '');
    setUpfront((d.upfrontFeeCents / 100).toString());
    setDeferred((d.deferredFeeCents / 100).toString());
    setCurrency(d.currency || 'aud');
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      api('/admin/settings/stripe', {
        method: 'PUT',
        json: {
          publishableKey: publishableKey.trim() || null,
          // Blank = leave unchanged on the server.
          secretKey: secretKey.trim() || null,
          webhookSecret: webhookSecret.trim() || null,
          upfrontFeeCents: Math.round((parseFloat(upfront) || 0) * 100),
          deferredFeeCents: Math.round((parseFloat(deferred) || 0) * 100),
          currency: currency.trim().toLowerCase() || 'aud',
        },
      }),
    onError: (e) => toast({ title: "Couldn't save Stripe settings", description: extractError(e), tone: 'error' }),
    onSuccess: () => {
      toast({ title: 'Stripe settings saved', description: 'Changes apply immediately — no restart needed.', tone: 'success' });
      setSecretKey('');
      setWebhookSecret('');
      qc.invalidateQueries({ queryKey: ['admin', 'stripe-settings'] });
    },
  });

  const d = settings.data;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        eyebrow="Reviewer console"
        title="Stripe"
        description="Card-payment keys and the lodgement-fee prices. Stored outside the app image and applied live — no redeploy. Secret keys are write-only: they're never shown back to you."
      />

      {settings.isLoading ? (
        <p className="text-[13px] text-(--color-ink-muted)">Loading settings…</p>
      ) : settings.isError ? (
        <p className="text-[13px] text-(--color-ink-muted)">Could not load settings (admin role required).</p>
      ) : (
        <form
          className="space-y-6"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <Card>
            <CardContent className="space-y-5 p-7">
              <div>
                <div className="section-ref">API keys</div>
                <h2 className="mt-1 font-serif text-xl font-medium">Stripe credentials</h2>
              </div>

              <Field
                id="pk"
                label="Publishable key"
                hint="Starts with pk_test_ or pk_live_. Safe to expose to the browser."
              >
                <Input
                  id="pk"
                  value={publishableKey}
                  onChange={(e) => setPublishableKey(e.target.value)}
                  placeholder="pk_live_…"
                  autoComplete="off"
                />
              </Field>

              <Field
                id="sk"
                label="Secret key"
                hint={
                  d?.secretKeySet
                    ? `A secret key is set (ending ${d.secretKeyLast4}). Leave blank to keep it.`
                    : 'Starts with sk_test_ or sk_live_. Server-only.'
                }
              >
                <Input
                  id="sk"
                  type="password"
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  placeholder={d?.secretKeySet ? '•••••••••• (unchanged)' : 'sk_live_…'}
                  autoComplete="off"
                />
              </Field>

              <Field
                id="whsec"
                label="Webhook signing secret"
                hint={
                  d?.webhookSecretSet
                    ? 'A signing secret is set. Leave blank to keep it. From the Stripe webhook endpoint (whsec_…).'
                    : 'From your Stripe webhook endpoint for /api/v1/stripe/webhook (whsec_…).'
                }
              >
                <Input
                  id="whsec"
                  type="password"
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                  placeholder={d?.webhookSecretSet ? '•••••••••• (unchanged)' : 'whsec_…'}
                  autoComplete="off"
                />
              </Field>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-5 p-7">
              <div>
                <div className="section-ref">Pricing</div>
                <h2 className="mt-1 font-serif text-xl font-medium">Lodgement fee</h2>
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <Field id="upfront" label="Pay-now fee" hint="Charged immediately by card.">
                  <MoneyInput id="upfront" value={upfront} onChange={setUpfront} currency={currency} />
                </Field>
                <Field id="deferred" label="From-refund fee" hint="Deferred — charged once the refund is confirmed.">
                  <MoneyInput id="deferred" value={deferred} onChange={setDeferred} currency={currency} />
                </Field>
              </div>

              <Field id="currency" label="Currency" hint="ISO code, lower-case (e.g. aud).">
                <Input
                  id="currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="max-w-[120px] uppercase"
                  maxLength={3}
                />
              </Field>
            </CardContent>
          </Card>

          <div className="flex items-center justify-between">
            <p className="flex items-center gap-1.5 text-[12.5px] text-(--color-ink-muted)">
              <ShieldCheck className="size-3.5" /> Keys are stored server-side, outside source control.
            </p>
            <Button type="submit" size="lg" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save settings'}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && <p className="text-[12px] text-(--color-ink-muted)">{hint}</p>}
    </div>
  );
}

function MoneyInput({
  id,
  value,
  onChange,
  currency,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  currency: string;
}) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[15px] font-medium text-(--color-ink-muted)">
        $
      </span>
      <Input
        id={id}
        type="number"
        min="0"
        step="1"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-7 pr-14"
      />
      <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[12px] font-semibold uppercase text-(--color-ink-muted)">
        {currency}
      </span>
    </div>
  );
}

function extractError(e: unknown): string {
  if (e instanceof ApiError) {
    const p = e.problem as { detail?: string; title?: string; errors?: Record<string, string[]> } | null;
    if (p?.errors) {
      const first = Object.values(p.errors).flat()[0];
      if (first) return first;
    }
    return p?.detail ?? p?.title ?? `Request failed (HTTP ${e.status})`;
  }
  return 'Request failed';
}
